import entries from 'lodash/fp/entries';
import filter from 'lodash/fp/filter';
import find from 'lodash/fp/find';
import first from 'lodash/fp/first';
import flow from 'lodash/fp/flow';
import get from 'lodash/fp/get';
import groupBy from 'lodash/fp/groupBy';
import has from 'lodash/fp/has';
import includes from 'lodash/fp/includes';
import isEmpty from 'lodash/fp/isEmpty';
import isEqual from 'lodash/fp/isEqual';
import keyBy from 'lodash/fp/keyBy';
import last from 'lodash/fp/last';
import map from 'lodash/fp/map';
import mapValues from 'lodash/fp/mapValues';
import matches from 'lodash/fp/matches';
import maxBy from 'lodash/fp/maxBy';
import omitBy from 'lodash/fp/omitBy';
import pickBy from 'lodash/fp/pickBy';
import size from 'lodash/fp/size';
import sumBy from 'lodash/fp/sumBy';
import uniq from 'lodash/fp/uniq';
import uniqBy from 'lodash/fp/uniqBy';
import values from 'lodash/fp/values';

export function getSubscribedSkusAndQuantities(subscription) {
  return flow(
    get('add_ons'),
    map(({ product, quantity }) => ({ sku: product.sku, quantity }))
  )(subscription);
}

function filterSelectedPods(selectedPods, valuesToFilter) {
  const isInFilterList = pod => find(matches({ id: pod.id }))(valuesToFilter);
  return omitBy(isInFilterList)(selectedPods);
}

export function filterRemovedSelectedItems(items) {
  return pickBy(
    flow(
      get('quantity'),
      quantity => quantity > 0
    )
  )(items);
}

export function getInitialSelectedPods({
  pods,
  selectedPods,
  subscription,
  flavorBannedSubscribedPods,
  unavailableSubscribedPods,
}) {
  const removedPods = getRemovedPods({
    pods,
    selectedPods,
    flavorBannedSubscribedPods,
    unavailableSubscribedPods,
  });

  if (!isEmpty(subscription)) {
    return {
      selectedPods: populateSelectedPodsFromSubscription(pods, subscription),
      removedPods,
    };
  }

  return {
    selectedPods: filterSelectedPods(selectedPods, removedPods),
    removedPods,
  };
}

export function getRemovedPods({
  pods,
  selectedPods,
  flavorBannedSubscribedPods,
  unavailableSubscribedPods,
}) {
  return uniqBy('id')([
    ...(flavorBannedSubscribedPods || []),
    ...(unavailableSubscribedPods || []),
    ...(isEmpty(pods) ? [] : values(filterSelectedPods(selectedPods, pods))),
  ]);
}

export function populateSelectedPodsFromSubscription(pods, subscription) {
  const subscribedSkus = getSubscribedSkusAndQuantities(subscription);
  const isInSubscription = pod => includes(pod.sku)(map('sku')(subscribedSkus));
  const getSubscriptionQuantity = pod =>
    flow(
      find(matches({ sku: pod.sku })),
      get('quantity')
    )(subscribedSkus);
  const toSelectedPod = pod => ({
    ...pod,
    quantity: getSubscriptionQuantity(pod),
  });

  return flow(
    filter(isInSubscription),
    map(toSelectedPod),
    keyBy('id')
  )(pods);
}

export function getMajoritySelectedNicotineStrength(selectedPodsById) {
  const nicotineSumsByStrength = flow(
    values,
    filter(has('nicotine_strength_percent')),
    groupBy('nicotine_strength_percent'),
    mapValues(sumBy('quantity'))
  )(selectedPodsById);

  const equalNicotineStrengthsSelected = flow(
    values,
    uniq,
    size,
    isEqual(1)
  )(nicotineSumsByStrength);

  if (size(nicotineSumsByStrength) > 1 && equalNicotineStrengthsSelected) {
    return;
  }

  return flow(
    entries,
    maxBy(last),
    first
  )(nicotineSumsByStrength);
}
